/**
 * Section Review Page (Phase 18.5.4)
 *
 * Manual section assignment review interface for trainers.
 * Displays triggers where AI could not match any SOP section,
 * allowing trainers to view the video, read the observation summary,
 * assign a section, and re-queue the trigger for analysis.
 */

import { useState } from 'react';
import {
  Layers,
  Video,
  Clock,
  User,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Send,
  Eye,
  History,
} from 'lucide-react';
import {
  useSectionReviews,
  useAssignSection,
  useSopSectionTasksLookup,
  type SectionReviewTrigger,
} from '../../hooks/useSectionReview';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDateTime(d: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(d));
}

function jobOption(section: SectionReviewTrigger['sections'][number]) {
  return (
    <option key={section.id} value={section.id}>
      Job {section.sectionNumber}: {section.title}
    </option>
  );
}

/**
 * Phase 36.1c-1 — what this trainer's org has confirmed before on this exact
 * camera + lens + SOP.
 *
 * COLD START IS THE DESIGN, not an afterthought:
 *   n = 0     render NOTHING. The card is byte-identical to pre-36.1c. Follows
 *             the 36.6b `cueLine` precedent — emit nothing rather than an empty
 *             label, because an empty label reads as a signal ("no history
 *             here" is a claim; silence is not).
 *   n = 1-2   render, but headed so the trainer weights it correctly. Two
 *             examples are an anecdote, and saying so is the honest framing.
 *   n = 3-9   normal panel.
 *   n >= 10   top 5 Jobs (server-capped) + "and N more".
 *
 * NEVER auto-selects the Job dropdown. Pre-filling would convert an
 * observation into an unlogged default and contaminate the label store with
 * clicks the trainer did not reason about — which is precisely the corpus this
 * panel exists to build.
 *
 * Mint, not amber: amber is already this page's "needs attention" colour and a
 * prior is informational.
 */
function PriorPanel({ trigger }: { trigger: SectionReviewTrigger }) {
  const { totalCount, sections: priorSections } = trigger.priors;

  if (totalCount === 0) return null;

  const titleFor = (sectionId: string) => {
    const section = trigger.sections.find((s) => s.id === sectionId);
    return section ? `Job ${section.sectionNumber}: ${section.title}` : null;
  };

  // A prior whose Job no longer exists (SOP reprocess) renders nothing rather
  // than a blank label. Counts are untouched, so the header stays truthful.
  const rows = priorSections
    .map((p) => ({ ...p, label: titleFor(p.sectionId) }))
    .filter((p): p is typeof p & { label: string } => p.label !== null);

  if (rows.length === 0) return null;

  const shownCount = rows.reduce((sum, r) => sum + r.count, 0);
  const remainder = totalCount - shownCount;

  return (
    <div className="rounded-xl border border-[#c5e8c2] bg-[#c5e8c2]/20 p-4">
      <div className="flex items-center gap-2 mb-2">
        <History size={14} className="text-[#00674a]" />
        <p className="text-xs font-semibold text-[#00674a] uppercase tracking-wide">
          {totalCount <= 2
            ? `Only ${totalCount} prior confirmation${totalCount === 1 ? '' : 's'} ${
                trigger.cameraId ? 'on this camera' : 'for this SOP'
              } — not yet a pattern`
            : `Previously confirmed ${trigger.cameraId ? 'here' : 'for this SOP'}`}
        </p>
      </div>
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <li key={row.sectionId} className="text-sm text-gray-800">
            <span className="font-medium">{row.label}</span>
            <span className="text-gray-600">
              {' '}
              — {row.count}×, last {formatRelativeTime(row.lastConfirmedAt)}
            </span>
            {row.tasks.length > 0 && (
              <ul className="mt-0.5 ml-4 space-y-0.5">
                {row.tasks.map((task) => (
                  <li key={task.taskId} className="text-xs text-gray-600">
                    Task {task.taskNumber}: {task.title} — {task.count}×
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {remainder > 0 && (
        <p className="mt-2 text-xs text-gray-500">and {remainder} more</p>
      )}
    </div>
  );
}

function TriggerCard({ trigger }: { trigger: SectionReviewTrigger }) {
  // Phase 36.1d — supervised triggers carry an AI Job pick the trainer is being
  // asked to judge. `aiPick` is null when the pick names a Job that no longer
  // exists (a SOP reprocess race), in which case the card degrades to the
  // ordinary `no_match` presentation rather than pointing at a ghost.
  const aiPick =
    trigger.reviewReason === 'supervised' && trigger.detectedSectionId
      ? trigger.sections.find((s) => s.id === trigger.detectedSectionId) ?? null
      : null;

  // PRE-SELECT the AI's pick — correct here and WRONG for `no_match`. Here the
  // model made an actual assertion the trainer is judging, so starting from it
  // is the question being asked. There, it made none, and pre-filling would
  // turn silence into an unlogged default.
  const [selectedSectionId, setSelectedSectionId] = useState(aiPick?.id ?? '');
  // Phase 36.1a: the trainer's optional Task pick. Reset whenever the Job
  // changes — a Task from the previous Job fails the assign handler's
  // job-level validation with a 400.
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const assignMutation = useAssignSection();

  // Phase 36.1a: Tasks for the currently selected Job. `enabled` keeps this
  // from firing until a Job is chosen, so the common "trainer is still
  // reading the observation" state issues no request at all.
  const { data: tasks } = useSopSectionTasksLookup(
    trigger.sopId,
    selectedSectionId || null
  );

  // Phase 36.4 (S4): surface the template's declared Jobs FIRST, and keep every
  // other Job selectable underneath. Ordering and labelling only — never a
  // restriction. Assigning an out-of-scope Job is the trainer's only way to
  // rescue a mis-scoped template from this queue, and it is the strongest signal
  // the system can get that the scope itself is wrong.
  //
  // Group only when the split is real. Every Job is inScope on an unscoped
  // trigger, and NO Job is when a SOP reprocess has deleted every section the
  // scope named — both of those render as the flat, pre-36.4 list rather than as
  // one group header sitting over the whole thing.
  const inScopeSections = trigger.sections.filter((s) => s.inScope);
  const otherSections = trigger.sections.filter((s) => !s.inScope);
  const showScopeGroups = inScopeSections.length > 0 && otherSections.length > 0;

  const handleAssign = () => {
    if (!selectedSectionId) return;
    assignMutation.mutate({
      triggerId: trigger.id,
      sectionId: selectedSectionId,
      // Empty string = "not chosen". The hook omits the field entirely, which
      // the API records as NULL. Task never gates the primary action.
      taskId: selectedTaskId || undefined,
    });
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50">
              <Layers size={18} className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                {trigger.sopTitle || 'Unknown SOP'}
              </h3>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {formatRelativeTime(trigger.triggeredAt)}
                </span>
                {trigger.operatorName && (
                  <span className="flex items-center gap-1">
                    <User size={12} />
                    {trigger.operatorName}
                  </span>
                )}
              </div>
            </div>
          </div>
          {aiPick ? (
            // Mint, not amber: "the AI has an answer, please check it" and
            // "the AI has no answer, please supply one" demand different
            // attention, and amber is this page's needs-attention colour.
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#c5e8c2] px-2.5 py-1 text-xs font-semibold text-[#00674a]">
              <Layers size={12} />
              Confirm Job
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-600">
              <Layers size={12} />
              Needs Section
            </span>
          )}
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Video Player */}
        {trigger.videoUrl ? (
          <div className="rounded-xl overflow-hidden bg-gray-900">
            <video
              src={trigger.videoUrl}
              controls
              className="w-full max-h-[400px] object-contain"
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        ) : (
          <div className="rounded-xl bg-gray-100 flex items-center justify-center h-48">
            <div className="text-center text-gray-400">
              <Video size={32} className="mx-auto mb-2" />
              <p className="text-sm">Video not available</p>
            </div>
          </div>
        )}

        {/* Time Range */}
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">
            <Clock size={14} className="text-gray-400" />
            {formatDateTime(trigger.startTime)} - {formatDateTime(trigger.endTime)}
          </span>
        </div>

        {/* Observation Summary */}
        {trigger.observationSummary && (
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye size={14} className="text-purple-500" />
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">AI Observation</p>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
              {trigger.observationSummary}
            </p>
          </div>
        )}

        {/* Phase 36.1d — the AI's actual assertion, which is the thing the
            trainer is being asked to judge. Confidence is omitted rather than
            rendered as NaN% when the pipeline took a path that records none. */}
        {aiPick && (
          <div className="rounded-xl border border-[#c5e8c2] bg-[#c5e8c2]/20 p-4">
            <p className="text-sm text-gray-800">
              AI matched:{' '}
              <span className="font-semibold">
                Job {aiPick.sectionNumber}: {aiPick.title}
              </span>
              {typeof trigger.detectedSectionConfidence === 'number' && (
                <span className="text-gray-600">
                  {' '}
                  ({Math.round(trigger.detectedSectionConfidence * 100)}% confidence)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Prior confirmations (Phase 36.1c-1) — context BEFORE the decision.
            Renders nothing at all when the corpus for this key is empty. */}
        <PriorPanel trigger={trigger} />

        {/* Section Selector and Re-analyze Button */}
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-gray-700">
            Assign Job (Section)
          </label>
          {trigger.sections.length > 0 ? (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <select
                  value={selectedSectionId}
                  onChange={(e) => {
                    setSelectedSectionId(e.target.value);
                    // Phase 36.1a: a Task belongs to exactly one Job, so a
                    // stale selection would 400 on submit. Clear it.
                    setSelectedTaskId('');
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green"
                  disabled={assignMutation.isPending}
                >
                  <option value="">Select a job...</option>
                  {showScopeGroups ? (
                    <>
                      <optgroup label="In template scope">
                        {inScopeSections.map(jobOption)}
                      </optgroup>
                      <optgroup label="Other Jobs in this SOP">
                        {otherSections.map(jobOption)}
                      </optgroup>
                    </>
                  ) : (
                    trigger.sections.map(jobOption)
                  )}
                </select>
                {trigger.sections.length > 0 && selectedSectionId && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    {trigger.sections.find(s => s.id === selectedSectionId)?.description || 'No description available'}
                  </p>
                )}

                {/*
                  Phase 36.1a — optional Task capture.

                  Rendered only once a Job is chosen AND that Job has at least
                  one Task: an empty select reads as a broken control, and a
                  Job with no Tasks is a normal shape. If the lookup fails the
                  select simply never appears — the Job assignment, which is
                  the primary action, must never be blocked on a secondary
                  fetch.

                  The helper text is not decoration. It is the honest statement
                  of the capture-only scope (blueprint §4.1) and stops the
                  trainer forming the false model that picking a Task narrows
                  what gets scored.
                */}
                {selectedSectionId && tasks && tasks.length > 0 && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Task (optional)
                    </label>
                    <select
                      value={selectedTaskId}
                      onChange={(e) => setSelectedTaskId(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green"
                      disabled={assignMutation.isPending}
                    >
                      <option value="">Select a task...</option>
                      {tasks.map((task) => (
                        <option key={task.id} value={task.id}>
                          Task {task.taskNumber}: {task.title}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-gray-500">
                      Recorded to improve future Job matching. Does not change what is scored.
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={handleAssign}
                disabled={!selectedSectionId || assignMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-[#00674a] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#00674a]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {assignMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {/* Phase 36.1d — the label states the COST before the click.
                    Confirming resumes from the stashed analysis for zero
                    Gemini cost; correcting re-runs the whole pipeline. */}
                {aiPick
                  ? selectedSectionId === aiPick.id
                    ? 'Confirm'
                    : 'Correct & Re-analyze'
                  : 'Re-analyze'}
              </button>
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3">
              <p className="text-sm text-amber-700">
                <AlertCircle size={14} className="inline mr-1.5" />
                No sections found for this SOP. The SOP may need to be reprocessed.
              </p>
            </div>
          )}
        </div>

        {/* Success message */}
        {assignMutation.isSuccess && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
            <p className="text-sm text-emerald-700 font-medium">
              {aiPick && selectedSectionId === aiPick.id
                ? 'Job confirmed. Assessment created from the existing analysis.'
                : 'Section assigned. Assessment has been re-queued for analysis.'}
            </p>
          </div>
        )}

        {/* Error message */}
        {assignMutation.isError && (
          <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3">
            <p className="text-sm text-red-700">
              <AlertCircle size={14} className="inline mr-1.5" />
              Failed to assign section. Please try again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SectionReviewPage() {
  useDocumentTitle('Job Review');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useSectionReviews(page);

  const triggers = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
            <Layers size={20} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Job Review</h1>
            <p className="text-sm text-gray-500">
              Assign SOP Jobs to triggers where AI could not find a match
            </p>
          </div>
        </div>
        {total > 0 && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700">
            {total} pending
          </span>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-24">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="text-center py-24 space-y-3">
          <AlertCircle size={40} className="mx-auto text-red-400" />
          <h2 className="text-lg font-semibold text-gray-900">Failed to load job reviews</h2>
          <p className="text-sm text-gray-500">Please try refreshing the page.</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && triggers.length === 0 && (
        <div className="text-center py-24 space-y-3">
          <Layers size={48} className="mx-auto text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-900">No triggers pending job review</h2>
          <p className="text-sm text-gray-500">
            All triggers have been matched to sections automatically.
          </p>
        </div>
      )}

      {/* Trigger Cards */}
      {!isLoading && !isError && triggers.length > 0 && (
        <div className="space-y-6">
          {triggers.map((trigger) => (
            <TriggerCard key={trigger.id} trigger={trigger} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
